import FbButton, {
  FbButtonSizes,
  FbButtonVariants,
} from 'components/FbUI/FbButton'
import React, { useState } from 'react'
import { BsCheck, BsChevronDown } from 'react-icons/bs'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command'
import { cn } from '../cn'

export interface ISelectItem<T = any> {
  label: string | React.JSX.Element
  labelWhenSelected?: string | React.JSX.Element
  icon?: React.JSX.Element
  value?: T
  hidden?: boolean
  [key: string]: any
}

export interface ISelectProps<T> {
  items?: ISelectItem<T>[]
  selected?: T
  onSelect?: (item: T, originalItem?: ISelectItem) => void
  error?: string
  nullSelectionLabel?: string
  buttonLabel?: string | React.JSX.Element
  buttonLabelClassName?: string
  buttonIcon?: React.JSX.Element
  buttonVariant?: FbButtonVariants
  menuLabel?: string
  disabled?: boolean
  defaultSelection?: T
  className?: string
  buttonClassName?: string
  disableChevronIcon?: boolean
  disableButtonText?: boolean
  buttonSize?: FbButtonSizes
  showSelectedItemFullLabelOnHover?: boolean
  searchPlaceholder?: string
  notFoundPlaceholder?: string
}

export function Select<T>(props: ISelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)

  function handleSelect(item: ISelectItem) {
    if (item.value === props?.selected) {
      if (props.defaultSelection) {
        props.onSelect?.(props.defaultSelection)
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        props.onSelect?.(undefined)
      }
    } else {
      props.onSelect?.(item.value, item)
    }
    setIsOpen(false)
  }

  const itemSelected = props.items?.find(
    (item) => item.value === props.selected
  )

  const selectionLabel =
    itemSelected?.labelWhenSelected ??
    itemSelected?.label ??
    props.nullSelectionLabel ??
    'All'

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger disabled={props.disabled} asChild>
        <FbButton
          className={cn('flex-1 border-[#DEE2E6]', props.buttonClassName)}
          variant={props.buttonVariant}
          size={props.buttonSize}
        >
          <div
            className={cn(
              'flex items-center justify-between gap-2 flex-1 min-w-0'
            )}
          >
            {props.disableButtonText ? null : (
              <>
                <div
                  className={
                    'flex items-center justify-start w-[90%] max-w-[90%]'
                  }
                >
                  {props.buttonIcon}
                  {props?.buttonLabel ? (
                    <>
                      <b
                        className={cn(
                          'shrink-0 min-w-fit',
                          props.buttonLabelClassName
                        )}
                      >
                        {props.buttonLabel}:&nbsp;
                      </b>
                    </>
                  ) : null}
                  {selectionLabel && (
                    <span
                      className={
                        'text-nowrap overflow-ellipsis overflow-hidden max-w-full'
                      }
                    >
                      {selectionLabel}
                    </span>
                  )}
                </div>
              </>
            )}
            {props.disableChevronIcon ? null : (
              <div>
                <BsChevronDown />
              </div>
            )}
          </div>
        </FbButton>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'w-[400px] p-0',
          {
            'opacity-50': props.disabled,
            'border-red-400': props.error,
          },
          props.className
        )}
      >
        <Command>
          <CommandInput
            placeholder={props.searchPlaceholder ?? 'Search items...'}
          />
          <CommandList>
            <CommandEmpty>
              {props.notFoundPlaceholder ?? 'No items found.'}
            </CommandEmpty>
            <CommandGroup>
              {props.items
                ?.filter((item) => !item.hidden)
                .map((item) => (
                  <CommandItem
                    key={String(item.value)}
                    value={String(item.label) + String(item.value)}
                    onSelect={() => handleSelect(item)}
                  >
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="flex items-center gap-2">
                        {item.icon}
                        {item.label}
                      </div>
                      <div>{props.selected === item.value && <BsCheck />}</div>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
